import * as yup from "yup";
import { UserPhoneCheckStatus } from "../../../common/enums";

// Define base schema to infer type from
const baseLoginFormSchema = yup.object({
  phoneNumber: yup.string().required().defined(),
  email: yup.string().nullable().defined(),
  firstName: yup.string().nullable().defined(),
  lastName: yup.string().nullable().defined(),
  birthDate: yup.date().nullable().defined(),
  password: yup.string().nullable().defined(),
  confirmPassword: yup.string().nullable().defined(),
  verificationCode: yup.string().nullable().defined(),
  acceptPrivacyPolicy: yup.bool().defined(),
  acceptTermsOfUse: yup.bool().defined(),
  acceptAutomatedEmails: yup.bool().defined(),
  acceptMarketingEmails: yup.bool().defined(),
  fontSizePercent: yup.number().default(100).defined(),
});

export type ILoginFormModel = yup.InferType<typeof baseLoginFormSchema>;

export const getLoginSchema = (
  isTermsOfUseRequired: boolean,
  status?: UserPhoneCheckStatus,
  isGoogleUser?: boolean,
  hasEmail?: boolean,
  isForgottenPasswordMode?: boolean
) => {
  const isConsentRequired =
    !isGoogleUser &&
    (status === UserPhoneCheckStatus.BrandNew ||
      status === UserPhoneCheckStatus.FirstLogin);

  const isPasswordWithComplexityRequired =
    !isForgottenPasswordMode &&
    (status === UserPhoneCheckStatus.BrandNew ||
      status === UserPhoneCheckStatus.FirstLogin);

  const isConfirmPasswordRequired =
    !isForgottenPasswordMode &&
    (status === UserPhoneCheckStatus.BrandNew ||
      status === UserPhoneCheckStatus.FirstLogin);

  return yup.object().shape({
    phoneNumber: yup
      .string()
      .required("Телефонът е задължителен.")
      .min(11, "Моля, въведете пълния си телефонен номер с +359 отпред")
      .defined(),

    firstName:
      status === UserPhoneCheckStatus.BrandNew
        ? yup
            .string()
            .min(3, "Невалидно малко име")
            .required("Полето 'Малко име' е задължително")
            .defined()
        : yup.string().nullable().defined(),

    lastName:
      status === UserPhoneCheckStatus.BrandNew
        ? yup
            .string()
            .min(3, "Невалидно фамилно име")
            .required("Полето 'Фамилно име' е задължително")
            .defined()
        : yup.string().nullable().defined(),

    birthDate: yup
      .date()
      .nullable()
      .max(new Date(), "Датата не може да бъде в бъдещето")
      .typeError("Невалидна дата")
      .defined(),

    email:
      status === UserPhoneCheckStatus.BrandNew ||
      (status === UserPhoneCheckStatus.FirstLogin && hasEmail === false)
        ? yup
            .string()
            .email("Невалиден имейл")
            .required("Имейлът е задължителен")
            .defined()
        : yup.string().nullable().defined(),

    password: isPasswordWithComplexityRequired
      ? yup
          .string()
          .required("Паролата е задължителна")
          .min(8, "Паролата трябва да е поне 8 символа")
          .matches(/[A-Z]/, "Паролата трябва да съдържа поне една главна буква")
          .matches(/[a-z]/, "Паролата трябва да съдържа поне една малка буква")
          .matches(/[0-9]/, "Паролата трябва да съдържа поне една цифра [0-9]")
          .defined()
      : status === UserPhoneCheckStatus.Regular && !isForgottenPasswordMode
      ? yup.string().required("Паролата е задължителна").defined()
      : yup.string().nullable().defined(),

    confirmPassword: isConfirmPasswordRequired
      ? yup
          .string()
          .required("Полето 'Потвърди парола' е задължително")
          .oneOf([yup.ref("password")], "Паролите не съвпадат")
          .defined()
      : yup.string().nullable().defined(),

    verificationCode:
      status === UserPhoneCheckStatus.PendingVerification
        ? yup
            .string()
            .required("Кодът е задължителен")
            .matches(/^\d{6}$/, "Кодът трябва да съдържа 6 цифри")
            .defined()
        : yup.string().nullable().defined(),

    acceptPrivacyPolicy: isConsentRequired
      ? yup
          .bool()
          .oneOf([true], "Необходимо е съгласие с Политиката за поверителност.")
          .defined()
      : yup.bool().defined(),

    acceptTermsOfUse:
      isConsentRequired && isTermsOfUseRequired
        ? yup.bool().oneOf([true], "Необходимо е съгласие с Общите условия.").defined()
        : yup.bool().defined(),

    acceptAutomatedEmails: isConsentRequired
      ? yup
          .bool()
          .oneOf(
            [true],
            "Необходимо е съгласие за получаване на системни съобщения."
          )
          .defined()
      : yup.bool().defined(),

    acceptMarketingEmails: yup.bool().defined(),

    fontSizePercent: yup.number().default(100).defined(),
  });
};

export const loginFormDefaultValues: ILoginFormModel = {
  phoneNumber: "",
  email: "",
  password: "",
  confirmPassword: "",
  verificationCode: "",
  birthDate: null,
  fontSizePercent: 100,
};
